import type { ChatbotApi } from '@liks/backend-svc-api/esm/apis/ChatbotApi';
import type { ChatbotTrainingExampleWithIdentity } from '../types/chatbot';
import { hasTrainingExampleIdentity } from './chatbotServiceGuards';

export async function listTrainingExamples(
  chatbotApi: ChatbotApi,
  chatbotId: string,
  options: {
    limit?: number;
    offset?: number;
    search?: string;
    sort?: string;
  } = {},
): Promise<ChatbotTrainingExampleWithIdentity[]> {
  const examples =
    await chatbotApi.deLiksChatbotBoundaryChatbotTrainingExampleResourceListTrainingExamples(
      {
        chatbotId,
        limit: options.limit ?? 1000,
        offset: options.offset ?? 0,
        sort: options.sort ?? 'modified',
        ...(options.search != null && options.search.length > 0
          ? { search: options.search }
          : {}),
      },
    );
  return examples.filter(hasTrainingExampleIdentity);
}

export async function listAllTrainingExamples(
  chatbotApi: ChatbotApi,
  chatbotId: string,
  onProgress?: (progress: { completed: number; total: number }) => void,
): Promise<ChatbotTrainingExampleWithIdentity[]> {
  const pageSize = 1000;
  const examples: ChatbotTrainingExampleWithIdentity[] = [];
  let offset = 0;

  for (;;) {
    const page = await listTrainingExamples(chatbotApi, chatbotId, {
      limit: pageSize,
      offset,
    });
    examples.push(...page);

    const hasMore = page.length >= pageSize;
    onProgress?.({
      completed: examples.length,
      total: hasMore ? examples.length + pageSize : examples.length,
    });

    if (!hasMore) {
      break;
    }

    offset += pageSize;
  }

  return examples;
}

export async function readTrainingExample(
  chatbotApi: ChatbotApi,
  chatbotId: string,
  exampleId: string,
): Promise<ChatbotTrainingExampleWithIdentity> {
  const example =
    await chatbotApi.deLiksChatbotBoundaryChatbotTrainingExampleResourceReadTrainingExample(
      { chatbotId, id: exampleId },
    );

  if (!hasTrainingExampleIdentity(example)) {
    throw new Error(
      'Backend returned a training example without id, question, and answer',
    );
  }

  return example;
}

export async function createTrainingExample(
  chatbotApi: ChatbotApi,
  input: {
    chatbotId: string;
    question: string;
    answer: string;
    sortOrder?: number | null;
  },
): Promise<ChatbotTrainingExampleWithIdentity> {
  const created =
    await chatbotApi.deLiksChatbotBoundaryChatbotTrainingExampleResourceCreateTrainingExample(
      {
        chatbotId: input.chatbotId,
        chatbotTrainingExample: {
          question: input.question.trim(),
          answer: input.answer.trim(),
          ...(input.sortOrder !== undefined
            ? { sortOrder: input.sortOrder }
            : {}),
        },
      },
    );

  if (!hasTrainingExampleIdentity(created)) {
    throw new Error(
      'Backend returned a training example without id, question, and answer',
    );
  }

  return created;
}

export async function updateTrainingExample(
  chatbotApi: ChatbotApi,
  input: {
    chatbotId: string;
    example: ChatbotTrainingExampleWithIdentity;
    question: string;
    answer: string;
    sortOrder: number | null;
  },
): Promise<ChatbotTrainingExampleWithIdentity> {
  const updated =
    await chatbotApi.deLiksChatbotBoundaryChatbotTrainingExampleResourceUpdateTrainingExample(
      {
        chatbotId: input.chatbotId,
        id: input.example.id,
        chatbotTrainingExample: {
          id: input.example.id,
          chatbotId: input.chatbotId,
          question: input.question.trim(),
          answer: input.answer.trim(),
          sortOrder: input.sortOrder,
          tenantId: input.example.tenantId ?? undefined,
        },
      },
    );

  if (!hasTrainingExampleIdentity(updated)) {
    throw new Error(
      'Backend returned a training example without id, question, and answer',
    );
  }

  return updated;
}

export async function deleteTrainingExample(
  chatbotApi: ChatbotApi,
  chatbotId: string,
  exampleId: string,
): Promise<void> {
  await chatbotApi.deLiksChatbotBoundaryChatbotTrainingExampleResourceDeleteTrainingExample(
    { chatbotId, id: exampleId },
  );
}
